import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, MapPin, Phone, Clock, Play, Square, PenLine, CheckCircle2, Loader2, CalendarDays, Navigation, RefreshCw, Footprints, Info, Camera, AlertTriangle, FileText, Eye, Search, Filter, Lock, CloudOff, Coffee, Car } from 'lucide-react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Skeleton } from '../ui/skeleton';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { toast } from 'sonner';
import { easerGet, easerPost, errorMessage, fmtTime, fmtDate, toISODate, fmtMinutes } from '../../lib/easerApi';
import { SignaturePad } from './SignaturePad';
import { EventDetailDialog, STATUS } from './EventDetailDialog';
import { ReportDialog } from './ReportDialog';
import { PhotoPromptDialog } from './PhotoPromptDialog';
import { IncidentDialog } from './IncidentDialog';
import { EaserPicker } from './EaserPicker';
import { isNetworkError, shouldQueue, queueReason } from '../../lib/offlineQueue';
import { clockSlot } from '../../lib/easerClock';
import { EvacuationDialog, isGreenEaser, saveEvacuation } from './EvacuationDialog';
import { getStoredUser } from '../../lib/easerApi';
import { AccStopDialog } from './AccStopDialog';
import { accActiveFor, resumeIfActive, startAcc, subscribeAcc } from '../../lib/accTracker';

const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const startOfWeek = (d) => { const x = new Date(d); const day = (x.getDay() + 6) % 7; x.setDate(x.getDate() - day); x.setHours(0, 0, 0, 0); return x; };
const sameDay = (a, b) => toISODate(a) === toISODate(b);

const mapsLink = (client) => client
  ? `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(`${client.address} ${client.zip} ${client.town}`)}`
  : null;

/**
 * paused: the pause in progress ({event_id, started_at, local}) or null. pauseUsed: slot ids whose single pause is
 * already taken. pendingPauses / pendingResumes / pendingClockIds: slot ids with pointages waiting in the offline queue.
 */
export function EaserPlanning({ onUnauthorized, active = null, locked = false, paused = null, pauseUsed = [], pendingEnds = [], pendingStarts = [], pendingPauses = [], pendingResumes = [], pendingClockIds = [], user = null }) {
  const green = isGreenEaser(user || getStoredUser());
  const [evacFor, setEvacFor] = useState(null); // slot waiting for the green-waste question before the end pointage
  // Accompagnement (bouton ACC) : traceur GPS du module accTracker, partagé avec toute l'application.
  const [acc, setAcc] = useState({ session: null, km: 0, gpsError: null });
  const [accStopFor, setAccStopFor] = useState(null); // {ev, thenEnd} : confirmation « ACC fin » (puis fin de prestation si thenEnd)
  useEffect(() => { resumeIfActive(); return subscribeAcc(setAcc); }, []);
  const accStart = async (ev) => {
    setBusyId(ev.id);
    try {
      const res = await startAcc(ev.id);
      if (res.warning) toast.warning(res.warning);
      else toast.success(res.resumed ? 'Accompagnement déjà en cours : suivi GPS repris' : 'Accompagnement démarré : le trajet est enregistré par GPS');
    } catch (err) {
      if (err?.response?.status === 401) return onUnauthorized();
      toast.error(errorMessage(err, "Impossible de démarrer l'accompagnement"));
    } finally {
      setBusyId(null);
    }
  };
  // Photos avant / après : règle du back-office (métiers concernés, moments, obligatoire) + rappel en attente.
  const [photoRules, setPhotoRules] = useState(null);
  const [photoPrompt, setPhotoPrompt] = useState(null); // {ev, kind, message, mandatory}
  const [photoReport, setPhotoReport] = useState(null); // {ev, kind} : ReportDialog ouvert depuis le rappel
  useEffect(() => {
    let alive = true;
    easerGet('/api/easer/photo-rules').then((r) => { if (alive) setPhotoRules(r.data); }).catch(() => {});
    return () => { alive = false; };
  }, []);
  const askPhotos = (ev, action) => {
    const kind = action === 'start' ? 'before' : action === 'end' ? 'after' : null;
    if (!kind || !photoRules?.applies || !photoRules[`${kind}_enabled`]) return false;
    if (ev.type_code !== 'AC_INT' || ev.is_tract) return false;
    setPhotoPrompt({ ev, kind, message: photoRules[`${kind}_message`], mandatory: !!photoRules.mandatory });
    return true;
  };
  const [mode, setMode] = useState('day');
  const [anchor, setAnchor] = useState(() => new Date());
  const [items, setItems] = useState(null);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [signing, setSigning] = useState(null);
  const [detailId, setDetailId] = useState(null);
  const [reporting, setReporting] = useState(null);
  const [incident, setIncident] = useState(null);
  const [viewing, setViewing] = useState(null); // null = my planning, else {id, name} of a colleague (read-only)
  const [typeFilter, setTypeFilter] = useState('all'); // all | intervention | tract | other
  const [statusFilter, setStatusFilter] = useState('all'); // all | planned | in_progress | done
  const [search, setSearch] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [webSession, setWebSession] = useState(true); // Dolibarr web session alive => pointage feeds MySchedule

  useEffect(() => {
    easerGet('/api/easer/me').then((res) => setWebSession(res.data.web_session !== false)).catch(() => {});
  }, []);

  const anchorKey = toISODate(anchor);
  const range = useMemo(() => {
    const a = new Date(`${anchorKey}T12:00:00`);
    return mode === 'day' ? { start: a, end: a } : { start: startOfWeek(a), end: addDays(startOfWeek(a), 6) };
  }, [mode, anchorKey]);

  const requestId = useRef(0);
  // Last planning fetched for each range is kept on the phone so the day's slots stay visible offline
  // (needed to clock the end of a prestation without network).
  const cacheKey = `easer_planning_cache_${viewing ? viewing.id : 'me'}_${toISODate(range.start)}_${toISODate(range.end)}`;
  const [offlineData, setOfflineData] = useState(false); // false | 'offline' | 'dolibarr' : cached planning shown

  const load = useCallback(async () => {
    const id = ++requestId.current;
    setError('');
    try {
      const params = { start: toISODate(range.start), end: toISODate(range.end) };
      if (viewing) params.user_id = viewing.id;
      const res = await easerGet('/api/easer/planning', params);
      if (id !== requestId.current) return; // a newer request superseded this one
      setItems(res.data.items);
      setOfflineData(false);
      try { localStorage.setItem(cacheKey, JSON.stringify({ at: Date.now(), items: res.data.items })); } catch { /* quota */ }
    } catch (err) {
      if (id !== requestId.current) return;
      if (err?.response?.status === 401) return onUnauthorized();
      if (shouldQueue(err)) {
        let cached = null;
        try { cached = JSON.parse(localStorage.getItem(cacheKey) || 'null'); } catch { cached = null; }
        if (cached?.items) {
          setItems((prev) => (prev && prev.length ? prev : cached.items));
          setOfflineData(queueReason(err));
          return;
        }
        setError(isNetworkError(err)
          ? 'Pas de réseau et aucun planning mémorisé pour cette période sur ce téléphone.'
          : 'Dolibarr est momentanément indisponible et ce planning n\'a pas encore été chargé sur ce téléphone. Il s\'affichera automatiquement à son retour.');
        setItems([]);
        return;
      }
      setError(errorMessage(err, 'Impossible de charger le planning'));
      setItems([]);
    }
  }, [range, viewing, onUnauthorized, cacheKey]);

  useEffect(() => { setItems(null); load(); }, [load]);

  // Reload automatically when the network comes back (the offline queue may have just been sent).
  useEffect(() => {
    const onOnline = () => load();
    window.addEventListener('online', onOnline);
    window.addEventListener('easer:clock-changed', onOnline); // a queued "fin de prestation" was just transmitted
    window.addEventListener('easer:dolibarr-restored', onOnline); // Dolibarr is back: refresh the cached view
    return () => { window.removeEventListener('online', onOnline); window.removeEventListener('easer:clock-changed', onOnline); window.removeEventListener('easer:dolibarr-restored', onOnline); };
  }, [load]);

  const shift = (n) => setAnchor((a) => addDays(a, mode === 'day' ? n : 7 * n));

  const clock = async (ev, action) => {
    // Accompagnement encore en cours : « ACC fin » (kilomètres → bon d'intervention) avant la fin de prestation.
    if (action === 'end' && accActiveFor(ev.id) && !ev.acc_asked) {
      setAccStopFor({ ev: { ...ev, acc_asked: true }, thenEnd: true });
      return;
    }
    // Green Easers: ask about green-waste removal before the end pointage (volume saved on the Dolibarr intervention).
    if (action === 'end' && green && ev.type_code === 'AC_INT' && !ev.evacuation_asked) {
      setEvacFor(ev);
      return;
    }
    setBusyId(ev.id);
    try {
      // An earlier pointage of this slot still on the phone (no network): queue this one too so the order is kept.
      const res = await clockSlot({ ev, action, mustQueue: action !== 'start' && pendingClockIds.includes(ev.id), onUnauthorized, onWebSessionLost: () => setWebSession(false) });
      if (res && !res.queued && !res.error) await load();
      // Pointage enregistré (ou gardé sur le téléphone) : proposer les photos avant / après selon la règle de l'agence.
      if (res && !res.error) askPhotos(ev, action);
    } finally {
      setBusyId(null);
    }
  };

  const title = mode === 'day'
    ? fmtDate(anchor.toISOString())
    : `Semaine du ${range.start.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })} au ${range.end.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}`;

  const kindOf = (ev) => (ev.is_tract ? 'tract' : ev.type_code === 'AC_INT' ? 'intervention' : 'other');
  const needle = search.trim().toLowerCase();
  const visible = (items || []).filter((ev) => (typeFilter === 'all' || kindOf(ev) === typeFilter)
    && (statusFilter === 'all' || ev.status === statusFilter)
    && (!needle || `${ev.label} ${ev.client?.name || ''} ${ev.client?.town || ''} ${ev.intervention?.ref || ''}`.toLowerCase().includes(needle)));
  const filtersActive = typeFilter !== 'all' || statusFilter !== 'all' || !!needle;
  const counts = (items || []).reduce((acc, ev) => { acc[kindOf(ev)] = (acc[kindOf(ev)] || 0) + 1; return acc; }, {});

  return (
    <div className="space-y-4" data-testid="easer-planning">
      <div className="flex items-center gap-2">
        <div className="flex rounded-lg border border-border bg-card p-0.5">
          {[['day', 'Jour'], ['week', 'Semaine']].map(([id, label]) => (
            <button key={id} type="button" onClick={() => setMode(id)} data-testid={`planning-mode-${id}`}
              className={`px-3 h-9 rounded-md text-sm font-medium transition-colors ${mode === id ? 'bg-[hsl(var(--primary))] text-white' : 'text-muted-foreground hover:text-foreground'}`}>
              {label}
            </button>
          ))}
        </div>
        <Button variant="outline" size="sm" className="ml-auto h-9" onClick={() => setAnchor(new Date())} data-testid="planning-today">Aujourd'hui</Button>
        <Button variant="ghost" size="icon" className="h-9 w-9" onClick={load} aria-label="Actualiser" data-testid="planning-refresh"><RefreshCw className="h-4 w-4" /></Button>
      </div>

      {!webSession && !viewing && (
        <div className="rounded-xl border border-[hsl(var(--accent))]/40 bg-[hsl(var(--accent))]/5 px-3 py-2 text-sm flex items-center gap-2" role="status" data-testid="planning-session-banner">
          <AlertTriangle className="h-4 w-4 text-[hsl(var(--accent))] flex-shrink-0" />
          <span className="flex-1">Session Dolibarr expirée : vos pointages ne seront pas comptés dans MySchedule tant que vous ne vous êtes pas reconnecté(e).</span>
          <button type="button" className="text-xs font-medium underline whitespace-nowrap" onClick={onUnauthorized} data-testid="planning-session-relogin">Se reconnecter</button>
        </div>
      )}

      <div className="flex items-center justify-between gap-2">
        <p className="text-xs text-muted-foreground">Planning affiché</p>
        <EaserPicker value={viewing} onChange={setViewing} onUnauthorized={onUnauthorized} />
      </div>
      {viewing && (
        <div className="rounded-xl border border-[hsl(var(--accent))]/40 bg-[hsl(var(--accent))]/5 px-3 py-2 text-sm flex items-center gap-2" role="status" data-testid="planning-readonly-banner">
          <Eye className="h-4 w-4 text-[hsl(var(--accent))] flex-shrink-0" />
          <span className="flex-1">Vous consultez le planning de <strong>{viewing.name}</strong> en lecture seule.</span>
          <button type="button" className="text-xs font-medium underline" onClick={() => setViewing(null)} data-testid="planning-back-to-mine">Mon planning</button>
        </div>
      )}

      <div className="flex items-center justify-between">
        <Button variant="ghost" size="icon" onClick={() => shift(-1)} aria-label="Précédent" data-testid="planning-prev"><ChevronLeft className="h-5 w-5" /></Button>
        <h2 className="font-['Space_Grotesk'] font-semibold first-letter:uppercase text-center text-base" data-testid="planning-title">{title}</h2>
        <Button variant="ghost" size="icon" onClick={() => shift(1)} aria-label="Suivant" data-testid="planning-next"><ChevronRight className="h-5 w-5" /></Button>
      </div>

      <div className="space-y-2" data-testid="planning-filters">
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="h-4 w-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Client, ville, service, FI..." className="h-9 pl-8" data-testid="planning-search" />
          </div>
          <Button variant={filtersActive ? 'default' : 'outline'} size="sm" className="h-9" onClick={() => setShowFilters((v) => !v)} data-testid="planning-filter-toggle">
            <Filter className="h-4 w-4 mr-1" /> Filtres{filtersActive ? ' •' : ''}
          </Button>
        </div>
        {showFilters && (
          <div className="rounded-xl border border-border bg-card p-3 space-y-2" data-testid="planning-filter-panel">
            <div className="flex flex-wrap gap-1.5">
              {[['all', `Tout (${items?.length || 0})`], ['intervention', `Interventions (${counts.intervention || 0})`], ['tract', `Tracts (${counts.tract || 0})`], ['other', `Autres (${counts.other || 0})`]].map(([id, label]) => (
                <button key={id} type="button" onClick={() => setTypeFilter(id)} data-testid={`planning-type-${id}`}
                  className={`h-8 px-3 rounded-full text-xs font-medium border transition-colors ${typeFilter === id ? 'bg-[hsl(var(--primary))] text-white border-[hsl(var(--primary))]' : 'border-border hover:bg-[hsl(var(--secondary))]'}`}>{label}</button>
              ))}
            </div>
            <div className="flex flex-wrap gap-1.5">
              {[['all', 'Tous statuts'], ['planned', 'À venir'], ['in_progress', 'En cours'], ['done', 'Terminées']].map(([id, label]) => (
                <button key={id} type="button" onClick={() => setStatusFilter(id)} data-testid={`planning-status-${id}`}
                  className={`h-8 px-3 rounded-full text-xs font-medium border transition-colors ${statusFilter === id ? 'bg-[hsl(var(--accent))] text-white border-[hsl(var(--accent))]' : 'border-border hover:bg-[hsl(var(--secondary))]'}`}>{label}</button>
              ))}
            </div>
            {filtersActive && <button type="button" className="text-xs underline text-muted-foreground" onClick={() => { setTypeFilter('all'); setStatusFilter('all'); setSearch(''); }} data-testid="planning-filter-reset">Réinitialiser les filtres</button>}
          </div>
        )}
      </div>

      {items === null && <div className="space-y-3">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-32 rounded-xl" />)}</div>}

      {items !== null && offlineData && (
        <p className="text-xs text-muted-foreground flex items-center gap-1 mb-2" data-testid="planning-offline-data"><CloudOff className="h-3.5 w-3.5" /> {offlineData === 'dolibarr' ? 'Dolibarr indisponible' : 'Hors ligne'} : planning affiché tel qu'au dernier chargement.</p>
      )}
      {items !== null && error && (
        <Card className="p-5 text-center">
          <p className="text-sm text-destructive mb-3" data-testid="planning-error">{error}</p>
          <Button variant="outline" size="sm" onClick={load}>Réessayer</Button>
        </Card>
      )}

      {items !== null && !error && items.length > 0 && visible.length === 0 && (
        <Card className="p-6 text-center" data-testid="planning-filter-empty">
          <Filter className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
          <p className="font-medium">Aucun créneau ne correspond aux filtres</p>
          <Button variant="outline" size="sm" className="mt-3" onClick={() => { setTypeFilter('all'); setStatusFilter('all'); setSearch(''); }}>Réinitialiser</Button>
        </Card>
      )}

      {items !== null && !error && items.length === 0 && (
        <Card className="p-8 text-center" data-testid="planning-empty">
          <CalendarDays className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
          <p className="font-medium">Aucune intervention {mode === 'day' ? 'ce jour' : 'cette semaine'}</p>
          <p className="text-sm text-muted-foreground mt-1">{viewing ? `Rien de planifié pour ${viewing.name} sur cette période.` : 'Votre planning Dolibarr est à jour.'}</p>
        </Card>
      )}

      {items !== null && visible.length > 0 && (
        <div className="space-y-3">
          {visible.map((ev, idx) => {
            const showDay = mode === 'week' && (idx === 0 || !sameDay(new Date(ev.start), new Date(visible[idx - 1].start)));
            return (
              <React.Fragment key={ev.id}>
                {showDay && <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground pt-2 capitalize">{fmtDate(ev.start)}</p>}
                <EventCard ev={ev} busy={busyId === ev.id} onClock={clock} onSign={() => setSigning(ev)} readonly={!!viewing}
                  lock={locked ? (ev.id === active?.id ? 'active' : 'other') : null} pendingEnd={pendingEnds.includes(ev.id)} pendingStart={pendingStarts.includes(ev.id)}
                  paused={!viewing && paused && paused.event_id === ev.id ? paused : null} pauseUsed={pauseUsed.includes(ev.id) || pendingPauses.includes(ev.id)}
                  pendingPause={pendingPauses.includes(ev.id)} pendingResume={pendingResumes.includes(ev.id)}
                  acc={acc.session && Number(acc.session.event_id) === ev.id ? acc : null} accElsewhere={!!acc.session && Number(acc.session.event_id) !== ev.id}
                  onAccStart={() => accStart(ev)} onAccStop={() => setAccStopFor({ ev, thenEnd: false })}
                  onDetails={() => setDetailId(ev.id)} onReport={() => setReporting(ev)} onIncident={() => setIncident(ev)} />
              </React.Fragment>
            );
          })}
        </div>
      )}

      <CompleteDialog ev={signing} onClose={() => setSigning(null)} onDone={() => { setSigning(null); load(); }} onUnauthorized={onUnauthorized} />
      <AccStopDialog open={!!accStopFor} ev={accStopFor?.ev || null} onClose={() => setAccStopFor(null)} onUnauthorized={onUnauthorized}
        onDone={async (res) => {
          const pending = accStopFor;
          setAccStopFor(null);
          if (!pending) return;
          // Dolibarr a recréé le créneau (bon repassé en brouillon puis revalidé) : on poursuit avec le nouvel identifiant.
          const changed = res?.event_id && Number(res.event_id) !== Number(pending.ev.id);
          const ev = changed ? { ...pending.ev, id: Number(res.event_id) } : pending.ev;
          if (pending.thenEnd) await clock(ev, 'end');
          else if (changed || res?.line) load();
        }} />
      <EventDetailDialog eventId={detailId} onClose={() => setDetailId(null)} onChanged={load} onUnauthorized={onUnauthorized} green={green} user={user} />
      <EvacuationDialog open={!!evacFor} ev={evacFor} mode="end" onClose={() => setEvacFor(null)} onUnauthorized={onUnauthorized}
        onProceed={async (volume) => {
          const ev = evacFor;
          setEvacFor(null);
          if (volume) {
            try { await saveEvacuation(ev, volume, { onUnauthorized }); } catch { return; }
          }
          await clock({ ...ev, evacuation_asked: true }, 'end');
        }} />
      <ReportDialog open={!!reporting} ev={reporting} onClose={() => setReporting(null)} onDone={() => { setReporting(null); load(); }} onUnauthorized={onUnauthorized} />
      <PhotoPromptDialog prompt={photoPrompt} onTake={() => { setPhotoReport({ ev: photoPrompt.ev, kind: photoPrompt.kind, mandatory: photoPrompt.mandatory, message: photoPrompt.message }); setPhotoPrompt(null); }} onSkip={() => setPhotoPrompt(null)} />
      <ReportDialog open={!!photoReport} ev={photoReport?.ev || null} mode={photoReport?.kind || null} intro={photoReport?.message || null} requirePhoto={!!photoReport?.mandatory}
        onClose={() => setPhotoReport(null)} onDone={() => { setPhotoReport(null); load(); }} onUnauthorized={onUnauthorized} />
      <IncidentDialog open={!!incident} ev={incident} onClose={() => setIncident(null)} onDone={() => { setIncident(null); load(); }} onUnauthorized={onUnauthorized} />
    </div>
  );
}

const PAUSED_STATUS = { label: 'En pause', className: 'bg-[hsl(var(--accent))]/15 text-[hsl(var(--accent))] border border-[hsl(var(--accent))]/40' };

/**
 * lock: null (free) | 'active' (this slot is the one in progress: only report, pause + end) | 'other' (another slot is in
 * progress: everything disabled). pendingEnd/pendingPause/pendingResume: that pointage waits in the offline queue.
 * paused: this slot is "en pause" (its FIN is clocked, a new DEBUT will be clocked on resume).
 */
function EventCard({ ev, busy, onClock, onSign, onDetails, onReport, onIncident, readonly = false, lock = null, pendingEnd = false, pendingStart = false, paused = null, pauseUsed = false, pendingPause = false, pendingResume = false, acc = null, accElsewhere = false, onAccStart, onAccStop }) {
  const isIntervention = ev.type_code === 'AC_INT' && !ev.is_tract; // pointage / signature only on intervention slots
  const blocked = lock === 'other';
  // A resume waiting on the phone puts the slot back "in progress" even if Dolibarr still shows its pause FIN.
  const inProgress = (ev.clock_in_event && !ev.clock_out_event) || pendingStart || pendingResume;
  const done = ev.status === 'done' && !pendingResume && !paused;
  const st = paused ? PAUSED_STATUS : pendingResume ? STATUS.in_progress : (STATUS[ev.status] || STATUS.planned);
  const canStart = ev.status === 'planned' && !ev.clock_in_event && !blocked && !pendingStart;
  const canEnd = inProgress && (ev.status !== 'done' || pendingResume) && !pendingEnd;
  // One pause per prestation, only on the slot in progress (its FIN is clocked, a new DEBUT at the resume).
  const canPause = lock === 'active' && canEnd && !pauseUsed;
  // Accompagnement (ACC) : trajet GPS facturé en km, seulement pendant la prestation en cours (pas en pause).
  const canAcc = isIntervention && canEnd && !paused && !readonly && !accElsewhere && typeof onAccStart === 'function';
  // Signature only once the slot is ended (or was never clocked), never while in progress or waiting for its end.
  const canSign = ev.status !== 'done' && !blocked && lock !== 'active' && !pendingEnd && !inProgress;
  const lockHint = blocked ? "Terminez d'abord la prestation en cours" : undefined;
  return (
    <Card className="p-4 border-border shadow-sm" data-testid="planning-event">
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="min-w-0">
          <p className="font-semibold leading-snug truncate" data-testid="event-label">{ev.label}</p>
          <p className="text-sm text-muted-foreground flex items-center gap-1 mt-0.5">
            <Clock className="h-3.5 w-3.5" /> {ev.fullday ? 'Journée entière' : `${fmtTime(ev.start)} – ${fmtTime(ev.end)}`}
            {ev.planned_minutes ? <span className="text-xs">· {fmtMinutes(ev.planned_minutes)} prévues</span> : null}
          </p>
          {ev.intervention && (
            <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5" data-testid="event-intervention">
              <FileText className="h-3 w-3" /> <span className="font-mono">{ev.intervention.ref}</span>
              {ev.intervention.slot_count > 1 && <span>· {ev.intervention.slot_count} créneaux</span>}
            </p>
          )}
        </div>
        <Badge className={`${st.className} hover:${st.className} flex-shrink-0`} data-testid="event-status">{st.label}</Badge>
      </div>

      {ev.client && (
        <div className="rounded-lg bg-[hsl(var(--secondary))]/60 p-3 text-sm space-y-1 mb-3" data-testid="event-client">
          <p className="font-medium">{ev.client.name}</p>
          {(ev.client.address || ev.client.town) && (
            <p className="text-muted-foreground flex items-start gap-1"><MapPin className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" /> {ev.client.address}{ev.client.address && ', '}{ev.client.zip} {ev.client.town}</p>
          )}
          <div className="flex flex-wrap gap-2 pt-1">
            {ev.client.phone && (
              <a href={`tel:${ev.client.phone}`} className="inline-flex items-center gap-1 text-[hsl(var(--primary))] font-medium" data-testid="event-client-phone"><Phone className="h-3.5 w-3.5" /> {ev.client.phone}</a>
            )}
            {mapsLink(ev.client) && (
              <a href={mapsLink(ev.client)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[hsl(var(--primary))] font-medium" data-testid="event-client-maps"><Navigation className="h-3.5 w-3.5" /> Itinéraire</a>
            )}
          </div>
        </div>
      )}
      {ev.location && !ev.client && <p className="text-sm text-muted-foreground flex items-center gap-1 mb-3"><MapPin className="h-3.5 w-3.5" /> {ev.location}</p>}
      {ev.note && <p className="text-sm text-muted-foreground mb-3">{ev.note}</p>}

      {paused && !readonly ? (
        <div className="space-y-2" data-testid="event-paused">
          <p className="text-sm flex items-center gap-1.5 text-[hsl(var(--accent))] font-medium">
            <Coffee className="h-4 w-4" /> En pause depuis {fmtTime(paused.started_at)}{pendingPause ? ' · pause enregistrée sur le téléphone, envoi à Dolibarr au retour du réseau' : ' · fin pointée dans Dolibarr'}
          </p>
          <Button onClick={() => onClock(ev, 'resume')} disabled={busy} className="w-full h-11 bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-white" data-testid="event-clock-resume">
            {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Play className="h-4 w-4 mr-1" />} Reprendre la prestation
          </Button>
          <p className="text-xs text-muted-foreground">La reprise pointe un nouveau début ; la prestation devra ensuite être clôturée par « Fin de prestation ».</p>
        </div>
      ) : done ? (
        <p className="text-sm flex items-center gap-1.5 text-[hsl(var(--brand-sage))] font-medium" data-testid="event-done">
          <CheckCircle2 className="h-4 w-4" /> {ev.is_tract ? 'Distribution terminée' : 'Intervention terminée'}{ev.signed ? ' · signée par le client' : ''}
        </p>
      ) : readonly ? (
        <p className="text-sm flex items-center gap-1.5 text-muted-foreground" data-testid="event-readonly">
          <Eye className="h-4 w-4" /> {ev.status === 'in_progress' ? 'Intervention en cours' : 'Intervention à venir'} · lecture seule
        </p>
      ) : !isIntervention && !ev.is_tract ? (
        <p className="text-sm flex items-center gap-1.5 text-muted-foreground" data-testid="event-no-pointage">
          <Info className="h-4 w-4" /> Événement d'agenda — pas de pointage (réservé aux interventions)
        </p>
      ) : pendingEnd ? (
        <p className="text-sm flex items-center gap-1.5 text-[hsl(var(--accent))] font-medium" data-testid="event-end-pending">
          <CloudOff className="h-4 w-4" /> {pendingStart ? 'Début et fin de prestation enregistrés' : 'Fin de prestation enregistrée'} sur le téléphone · envoi à Dolibarr au retour du réseau
        </p>
      ) : blocked && isIntervention ? (
        <p className="text-sm flex items-center gap-1.5 text-muted-foreground" data-testid="event-locked">
          <Lock className="h-4 w-4" /> Terminez d'abord la prestation en cours pour agir sur ce créneau
        </p>
      ) : ev.is_tract ? (
        <Link to={`/easer?tab=tracts&event=${ev.id}`} className="block">
          <Button className="w-full h-11 bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))]/90 text-white" data-testid="event-start-tract">
            <Footprints className="h-4 w-4 mr-1" /> Démarrer la distribution de tracts
          </Button>
        </Link>
      ) : (
        <div className="grid grid-cols-2 gap-2">
          {canStart && (
            <Button onClick={() => onClock(ev, 'start')} disabled={busy} className="h-11 bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-white" data-testid="event-clock-start">
              {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Play className="h-4 w-4 mr-1" />} Pointer début
            </Button>
          )}
          {pendingStart && (
            <p className="col-span-2 text-sm flex items-center gap-1.5 text-[hsl(var(--accent))] font-medium" data-testid="event-start-pending">
              <CloudOff className="h-4 w-4" /> Début de prestation enregistré sur le téléphone · envoi à Dolibarr au retour du réseau
            </p>
          )}
          {pendingResume && (
            <p className="col-span-2 text-sm flex items-center gap-1.5 text-[hsl(var(--accent))] font-medium" data-testid="event-resume-pending">
              <CloudOff className="h-4 w-4" /> Reprise de prestation enregistrée sur le téléphone · envoi à Dolibarr au retour du réseau
            </p>
          )}
          {canAcc && (
            <div className="col-span-2 space-y-1" data-testid="event-acc">
              {acc ? (
                <Button onClick={onAccStop} disabled={busy} className="w-full h-10 bg-[hsl(var(--accent))]/15 hover:bg-[hsl(var(--accent))]/25 text-[hsl(var(--accent))] border border-[hsl(var(--accent))]/40 justify-between" data-testid="event-acc-stop">
                  <span className="flex items-center gap-1.5"><span className="relative flex h-2 w-2"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[hsl(var(--accent))] opacity-75" /><span className="relative inline-flex rounded-full h-2 w-2 bg-[hsl(var(--accent))]" /></span><Car className="h-4 w-4" /> ACC fin</span>
                  <span className="font-mono text-sm" data-testid="event-acc-km">{Number(acc.km || 0).toLocaleString('fr-FR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} km</span>
                </Button>
              ) : (
                <Button onClick={onAccStart} disabled={busy} variant="outline" size="sm" className="w-full h-9 text-xs" title="Accompagnement du client : le trajet est tracé par GPS et les km ajoutés au bon d'intervention" data-testid="event-acc-start">
                  <Car className="h-3.5 w-3.5 mr-1" /> ACC · accompagnement (trajet + km)
                </Button>
              )}
              {acc?.gpsError && <p className="text-[11px] text-destructive flex items-center gap-1" data-testid="event-acc-gps-error"><AlertTriangle className="h-3 w-3" /> {acc.gpsError}</p>}
            </div>
          )}
          {canPause && (
            <Button onClick={() => onClock(ev, 'pause')} disabled={busy} variant="outline" className="h-11 border-[hsl(var(--accent))]/50 text-[hsl(var(--accent))] hover:text-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))]/10" title="Pointe la fin ; la reprise pointera un nouveau début" data-testid="event-clock-pause">
              {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Coffee className="h-4 w-4 mr-1" />} Pause
            </Button>
          )}
          {canEnd && (
            <Button onClick={() => onClock(ev, 'end')} disabled={busy} variant={lock === 'active' ? 'default' : 'outline'} className={`h-11 ${lock === 'active' ? `${canPause ? '' : 'col-span-2'} bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))]/90 text-white` : ''}`} data-testid="event-clock-end">
              {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Square className="h-4 w-4 mr-1" />} {lock === 'active' ? 'Fin de prestation' : 'Pointer fin'}
            </Button>
          )}
          {canSign && (
            <Button onClick={onSign} variant={canStart ? 'outline' : 'default'} className={`h-11 ${canStart || canEnd ? '' : 'col-span-2'} ${!canStart && !canEnd ? 'bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))]/90 text-white' : ''}`} data-testid="event-sign">
              <PenLine className="h-4 w-4 mr-1" /> Terminer + signature
            </Button>
          )}
        </div>
      )}

      {!readonly && <div className="grid grid-cols-3 gap-1 mt-3 pt-3 border-t border-border/60">
        <Button variant="ghost" size="sm" className="h-10 text-xs" onClick={onDetails} data-testid="event-details"><Info className="h-4 w-4 mr-1" /> Détails</Button>
        {isIntervention ? <Button variant={lock === 'active' ? 'outline' : 'ghost'} size="sm" className="h-10 text-xs" onClick={onReport} disabled={blocked} title={lockHint} data-testid="event-report"><Camera className="h-4 w-4 mr-1" /> Compte-rendu</Button> : <span />}
        {isIntervention && ev.status !== 'done' && lock !== 'active' ? (
          <Button variant="ghost" size="sm" className="h-10 text-xs text-[hsl(var(--accent))] hover:text-[hsl(var(--accent))]" onClick={onIncident} disabled={blocked} title={lockHint} data-testid="event-incident"><AlertTriangle className="h-4 w-4 mr-1" /> Imprévu</Button>
        ) : <span />}
      </div>}
    </Card>
  );
}

function CompleteDialog({ ev, onClose, onDone, onUnauthorized }) {
  const padRef = useRef(null);
  const [clientName, setClientName] = useState('');
  const [comment, setComment] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (ev) { setClientName(ev.client?.name || ''); setComment(''); } }, [ev]);

  const submit = async () => {
    const signature = padRef.current?.toDataURL();
    if (!signature) return toast.error('La signature du client est requise');
    setSaving(true);
    try {
      const res = await easerPost(`/api/easer/events/${ev.id}/complete`, { signature, client_name: clientName, comment });
      toast.success(res.data.uploaded_to_dolibarr ? 'Intervention terminée, signature enregistrée dans Dolibarr' : 'Intervention terminée (signature conservée sur le site)');
      onDone();
    } catch (err) {
      if (err?.response?.status === 401) return onUnauthorized();
      toast.error(errorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!ev} onOpenChange={(o) => { if (!o && !saving) onClose(); }}>
      <DialogContent className="max-w-md w-[95vw] rounded-2xl" data-testid="complete-dialog">
        <DialogHeader>
          <DialogTitle className="font-['Space_Grotesk']">Terminer l'intervention</DialogTitle>
          <DialogDescription>{ev?.label} — le client confirme la réalisation de la prestation en signant.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="complete-client">Nom du signataire</Label>
            <Input id="complete-client" value={clientName} onChange={(e) => setClientName(e.target.value)} className="h-11" data-testid="complete-client-name" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="complete-comment">Remarque (optionnel)</Label>
            <Textarea id="complete-comment" value={comment} onChange={(e) => setComment(e.target.value)} rows={2} placeholder="Produits à prévoir, observation..." data-testid="complete-comment" />
          </div>
          <div className="space-y-1.5">
            <Label>Signature du client</Label>
            {ev && <SignaturePad ref={padRef} height={170} />}
          </div>
        </div>
        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={onClose} disabled={saving} data-testid="complete-cancel">Annuler</Button>
          <Button onClick={submit} disabled={saving} className="bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-white" data-testid="complete-submit">
            {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-1" />} Valider
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
